import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { planService } from "../services/planService";
import { hasActiveSuccessfulPlanPayment } from "../utils/marketplaceAccess";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Clock3,
  Loader2,
  RefreshCcw,
  XCircle,
} from "lucide-react";

const formatCurrency = (amount) => `N${Number(amount || 0).toLocaleString()}`;

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function ManagePlan() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");

  const activePlanQuery = useQuery({
    queryKey: ["active-plan-payment", user?.id],
    queryFn: async () => planService.getActivePlanPayment(user!.id),
    enabled: Boolean(user?.id) && Boolean(user?.token),
  });

  const cancelMutation = useMutation({
    mutationFn: async () => planService.cancelActivePlanPayment(),
  });

  const activePlanPayment = activePlanQuery.data || null;
  const plan = activePlanPayment?.plan || null;
  const markets = Array.isArray(plan?.markets) ? plan.markets : [];
  const isSuccessful = hasActiveSuccessfulPlanPayment(activePlanPayment);
  const isCanceled = activePlanPayment?.isCanceled === true;
  const isExpired = activePlanPayment?.isExpired === true;
  const canCancel =
    Boolean(activePlanPayment) && !isSuccessful && !isCanceled && !isExpired;

  const statusTone = isSuccessful
    ? "emerald"
    : isCanceled
      ? "rose"
      : isExpired
        ? "amber"
        : "violet";

  const statusLabel = isSuccessful
    ? "Active plan"
    : isCanceled
      ? "Payment canceled"
      : isExpired
        ? "Payment expired"
        : "Pending payment";

  const loadError = activePlanQuery.error
    ? getApiErrorMessage(activePlanQuery.error, "Could not load plan state.")
    : "";

  const handleCancelPendingPayment = async () => {
    if (!canCancel || cancelMutation.isPending) {
      return;
    }

    setActionError("");
    setActionSuccess("");

    try {
      await cancelMutation.mutateAsync();
      await queryClient.invalidateQueries({
        queryKey: ["active-plan-payment", user?.id],
      });
      setActionSuccess(
        "The pending plan payment has been canceled. You can restart seller onboarding to choose another plan.",
      );
    } catch (error) {
      setActionError(
        getApiErrorMessage(error, "Could not cancel the active plan payment."),
      );
    }
  };

  if (activePlanQuery.isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="mx-auto animate-spin text-violet-500" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Loading plan state...
          </p>
        </div>
      </div>
    );
  }

  if (!activePlanPayment) {
    return (
      <div className="space-y-6 max-w-3xl mx-auto">
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 text-sm font-semibold text-violet-600 transition hover:text-violet-500 dark:text-violet-300 dark:hover:text-violet-200">
          <ArrowLeft size={15} /> Back to dashboard
        </button>

        <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-10 text-center dark:border-gray-700 dark:bg-gray-900">
          <AlertCircle size={40} className="mx-auto text-gray-300 dark:text-gray-700" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
            No active plan payment found
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            The current seller contract exposes plan inspection and pending-plan
            cancellation. If you need to choose a plan, continue from the
            marketplace pricing screen.
          </p>
          <button
            type="button"
            onClick={() => navigate("/dashboard/pricing")}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500">
            View pricing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-2 text-sm font-semibold text-violet-600 transition hover:text-violet-500 dark:text-violet-300 dark:hover:text-violet-200">
            <ArrowLeft size={15} /> Back to dashboard
          </button>
          <p className="mt-4 text-xs font-semibold uppercase tracking-[0.24em] text-violet-500">
            Plan State
          </p>
          <h1 className="mt-2 text-3xl font-black text-gray-900 dark:text-gray-100">
            {plan?.name || "Current plan"}
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
            This page reflects the active plan payment contract directly. It can
            inspect current plan payment state and cancel a pending payment, but
            it does not attempt unsupported in-place plan edits.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/dashboard/pricing")}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800">
          <RefreshCcw size={15} />
          View pricing
        </button>
      </div>

      {(loadError || actionError || actionSuccess) && (
        <div
          className={`rounded-2xl border p-4 text-sm ${actionSuccess
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
              : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200"
            }`}>
          {actionSuccess || actionError || loadError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <PlanCard
          label="Status"
          value={statusLabel}
          hint={isSuccessful ? "Provisioning can continue" : "No seller provisioning yet"}
        />
        <PlanCard
          label="Price"
          value={formatCurrency(plan?.price)}
          hint={plan?.price === 0 ? "Free plan" : "Per plan payment"}
        />
        <PlanCard
          label="Product Cap"
          value={String(plan?.maxProducts || 0)}
          hint="Maximum products allowed"
        />
        <PlanCard
          label="Category Cap"
          value={String(plan?.maxCategories || 0)}
          hint="Maximum approved categories"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center gap-3">
            {statusTone === "emerald" ? (
              <CheckCircle size={20} className="text-emerald-500" />
            ) : statusTone === "rose" ? (
              <XCircle size={20} className="text-rose-500" />
            ) : (
              <Clock3
                size={20}
                className={statusTone === "amber" ? "text-amber-500" : "text-violet-500"}
              />
            )}
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Active plan payment state
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Transaction reference, expiry, and request linkage from the
                active plan payment endpoint.
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <PlanInfoRow label="Plan name" value={plan?.name || "-"} />
            <PlanInfoRow
              label="Transaction reference"
              value={activePlanPayment?.transactionReference || "-"}
            />
            <PlanInfoRow
              label="Expires at"
              value={formatDateTime(activePlanPayment?.expiresAt)}
            />
            <PlanInfoRow
              label="Linked request"
              value={activePlanPayment?.requestId || activePlanPayment?.request?.id || "None yet"}
            />
          </div>

          {canCancel && (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                This payment has not completed provisioning yet.
              </p>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                If you want to switch plans, cancel this pending payment and
                return to the marketplace pricing screen.
              </p>
              <button
                type="button"
                onClick={handleCancelPendingPayment}
                disabled={cancelMutation.isPending}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60">
                {cancelMutation.isPending && (
                  <Loader2 size={15} className="animate-spin" />
                )}
                Cancel pending payment
              </button>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Plan coverage
          </h2>
          <div className="mt-5 space-y-4">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
                Eligible markets
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {markets.length > 0 ? (
                  markets.map((market) => (
                    <span
                      key={market.id}
                      className="rounded-full bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-700 dark:bg-violet-500/10 dark:text-violet-200">
                      {market.name}
                      {market.city ? `, ${market.city}` : ""}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No markets were returned for this plan.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
                Provisioning note
              </p>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                A successful payment does not guarantee immediate seller
                provisioning. The frontend should continue to poll the profiler
                record until `me.seller` becomes non-null.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function PlanCard({ label, value, hint }) {
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-gray-100">
        {value}
      </p>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{hint}</p>
    </article>
  );
}

function PlanInfoRow({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-2 break-words text-sm font-semibold text-gray-900 dark:text-gray-100">
        {value}
      </p>
    </div>
  );
}
